import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard,
  Building2,
  BarChart3,
  UserCircle2,
  LogOut,
  Menu,
  ScanLine,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  {
    to: "/customer/dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    testId: "portal-nav-dashboard",
  },
  {
    to: "/customer/business",
    label: "Meine Firma",
    icon: Building2,
    testId: "portal-nav-business",
  },
  {
    to: "/customer/statistik",
    label: "Statistik",
    icon: BarChart3,
    testId: "portal-nav-stats",
  },
  {
    to: "/customer/konto",
    label: "Konto",
    icon: UserCircle2,
    testId: "portal-nav-account",
  },
];

function NavItems({ onNavigate }) {
  return (
    <nav className="flex flex-col gap-1">
      {NAV.map(({ to, label, icon: Icon, testId }) => (
        <NavLink
          key={to}
          to={to}
          end
          onClick={onNavigate}
          data-testid={testId}
          className={({ isActive }) =>
            cn(
              "group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
              "hover:bg-white/[0.04]",
              isActive
                ? "bg-primary/10 text-primary border border-primary/20"
                : "text-muted-foreground border border-transparent"
            )
          }
        >
          <Icon className="w-4 h-4" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

function BrandMark() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-blue-500 grid place-items-center shadow-lg shadow-primary/20">
        <ScanLine className="w-4 h-4 text-primary-foreground" />
      </div>
      <div className="leading-none">
        <div className="font-heading font-bold text-lg tracking-tight">1AI Review</div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">
          Kundenportal
        </div>
      </div>
    </div>
  );
}

export default function PortalShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-white/[0.06] bg-card/60 backdrop-blur-xl px-4 py-6"
        data-testid="portal-sidebar"
      >
        <div className="px-1">
          <BrandMark />
        </div>
        <div className="mt-8 flex-1">
          <NavItems />
        </div>
        <div className="mt-6 border-t border-white/[0.06] pt-4">
          <div className="px-3 py-1 text-sm truncate">{user?.name}</div>
          <div className="px-3 pb-2 text-xs text-muted-foreground truncate">
            {user?.email}
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground"
            onClick={handleLogout}
            data-testid="portal-nav-logout"
          >
            <LogOut className="w-4 h-4" /> Abmelden
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 bg-background/90 backdrop-blur-xl border-b border-white/[0.06] px-4 py-3 flex items-center justify-between">
        <BrandMark />
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="portal-mobile-menu-toggle">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-card border-white/[0.06] w-72">
            <div className="mb-6">
              <BrandMark />
            </div>
            <NavItems onNavigate={() => setMobileOpen(false)} />
            <div className="mt-6 border-t border-white/[0.06] pt-4">
              <div className="px-3 py-1 text-sm truncate">{user?.name}</div>
              <div className="px-3 pb-2 text-xs text-muted-foreground truncate">
                {user?.email}
              </div>
              <Button
                variant="ghost"
                className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setMobileOpen(false);
                  handleLogout();
                }}
                data-testid="portal-mobile-nav-logout"
              >
                <LogOut className="w-4 h-4" /> Abmelden
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </header>

      <main className="lg:pl-64">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10 animate-fade-in">
          {children}
        </div>
      </main>
    </div>
  );
}
